/**
 * @description
 * 具体活动相关信息
 * 1. 查活动任务礼包和相对档位
 * 2. 查用户当前活动任务进度
 * 3. 通过以上2种信息进行计算并渲染
 */

import { computed, ref, toRefs, watch } from "@vue/composition-api"
import { createNamespacedHelpers } from "vuex-composition-helpers"
import { TaskProcessQueryBodyInfo, TaskProcQueryBodyRuleItem, TaskProcQueryBodyRuleItemPresentItem } from "../../api"
import { AppleGiftCardActions, NewbieTaskDetail, NewbiePresentStatus, AppleLadderTaskDetail } from "../../store/apple-gift-card/actions"
import { AppleGiftCardGetters } from "../../store/apple-gift-card/getters"
import { AppleGiftCardMutations } from "../../store/apple-gift-card/mutations"
import { AppleGiftCardState } from "../../store/apple-gift-card/state"
import { REPORT } from "../../config"
import { format, isValid, subSeconds, formatDistanceStrict, startOfMonth } from "date-fns"
import { checkTimeValid } from "../../../../utils/helper"
import { GiftTaskConfigItemResult } from "./useGiftTask"
import MUTATION_TYPES from "../../store/apple-gift-card/mutation-types"

const { useActions, useState, useMutations } = createNamespacedHelpers<
  AppleGiftCardState,
  AppleGiftCardGetters,
  AppleGiftCardActions,
  AppleGiftCardMutations
>("appleGiftCard")

interface GiftTaskItemProps {
  props: {
    isShow: boolean
    taskConfig: GiftTaskConfigItemResult
    totalTaskError: boolean
    ccbScore: {
      weekCcbScore: number
      monthCcbScore: number
    }
    ccbWeekProcess?: TaskProcessQueryBodyInfo | null
    ccbMonthProcess?: TaskProcessQueryBodyInfo | null
  }
  emit: ((event: string, ...args: any[]) => void) | ((event: string, ...args: any[]) => void)
}
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default ({ props, emit }: GiftTaskItemProps) => {
  const { isShow, taskConfig, totalTaskError, ccbScore, ccbWeekProcess, ccbMonthProcess } = toRefs(props)
  const { giftTaskConfig, isLoggedIn, selectedZone, selectedRole } = useState([
    "giftTaskConfig",
    "isLoggedIn",
    "selectedZone",
    "selectedRole"
  ])
  const {
    taskProcQuery,
    ccbTaskProcQuery,
    getTaskPresent,
    setUserZoneRoleFromCGIData,
    setToastMsg,
    PersistZoneRole,
    reportUserAction,
    reportError,
    getNewbieTask,
    getLadderTask
  } = useActions([
    "ccbTaskProcQuery",
    "taskProcQuery",
    "getTaskPresent",
    "setUserZoneRoleFromCGIData",
    "setToastMsg",
    "PersistZoneRole",
    "reportUserAction",
    "reportError",
    "getNewbieTask",
    "getLadderTask"
  ])
  const mutations = useMutations([MUTATION_TYPES.CLEAR_ZONE])
  const monthConfig = computed(() => taskConfig.value.monthConfig)
  const showTips = ref(false)
  const showItemTips = ref("")
  const weekProcess = ref<TaskProcessQueryBodyInfo | null>(taskConfig.value.weekProcess || null)
  const monthProcess = ref<TaskProcessQueryBodyInfo | null>(taskConfig.value.monthProcess || null)
  const weekTask = ref<TaskProcQueryBodyRuleItem | null>(null)
  const weekCcbTask = ref<TaskProcQueryBodyRuleItem | null>(null)
  const monthTask = ref<TaskProcQueryBodyRuleItem | null>(null)
  const monthCcbTask = ref<TaskProcQueryBodyRuleItem | null>(null)
  const requestFailed = ref(false)
  const isLoading = ref(false)
  const newbieTask = ref<NewbieTaskDetail | null>(null)
  const newbieTaskPresentStatus = NewbiePresentStatus
  const ladderTask = ref<AppleLadderTaskDetail | null>(null)
  // 物品框弹窗
  const handleShowTipsBox = (itemTipsName: string) => {
    showItemTips.value = itemTipsName
  }

  // 月任务有效
  const monthTaskValid = computed(() => monthConfig.value && checkTimeValid(monthConfig.value.beginTime, monthConfig.value.endTime))

  // 已领取周礼包
  const hasReceived = computed(() => {
    if (weekProcess.value?.present_rank) return weekProcess.value.present_rank.split(",")
    return []
  })

  // 已领取月礼包
  const hasMonthlyReceived = computed(() => {
    if (monthProcess.value?.present_rank) return monthProcess.value.present_rank.split(",")
    return []
  })

  // 周充充币
  const weekCcbScore = computed(() => Number(ccbWeekProcess?.value?.total_score) || 0)

  // 周花花币
  const weekHhbScore = computed(() => Number(weekProcess.value?.total_score))

  // 周任务进度
  const computeWeeklyProcess = computed(() => {
    if (isWeeklyRegistered.value && weekTask.value && weekCcbTask.value && weekProcess.value) {
      const { present_item } = weekTask.value
      const ccb_present_item = weekCcbTask.value.present_item

      const prePresentItem: TaskProcQueryBodyRuleItemPresentItem[] = []
      const postPresentItem: TaskProcQueryBodyRuleItemPresentItem[] = []
      const activePresentItem: TaskProcQueryBodyRuleItemPresentItem[] = []

      present_item.forEach(item => {
        if (hasReceived.value.includes(item.num)) {
          prePresentItem.push(item)
        } else {
          postPresentItem.push(item)
        }
      })
      const postLength = postPresentItem.length
      const preLength = prePresentItem.length
      for (let i = 0; i < postLength; i++) {
        const nextHhbItemThreshold = Number(postPresentItem[0].num)
        const nextCcbItemThreshold = Number(ccb_present_item?.[preLength + i].num)
        if (weekCcbScore.value >= nextCcbItemThreshold && weekHhbScore.value >= nextHhbItemThreshold) {
          activePresentItem.push(postPresentItem.shift() as TaskProcQueryBodyRuleItemPresentItem)
        } else {
          break
        }
      }

      return {
        prePresentItem,
        activePresentItem,
        postPresentItem
      }
    } else {
      return { prePresentItem: weekTask.value?.present_item }
    }
  })

  // 已开启周任务
  const isWeeklyRegistered = computed(() => !!weekProcess.value)

  // 已开启月任务
  const isMonthlyRegistered = computed(() => !!monthProcess.value)

  // 周礼包已经全部领取完
  const isCompleted = computed(
    () =>
      computeWeeklyProcess.value.prePresentItem && computeWeeklyProcess.value.prePresentItem?.length === weekTask.value?.present_item.length
  )

  // 一键领取文本
  const receivableText = computed(() => {
    if (!weekTask.value || !weekCcbTask.value || !weekProcess.value) return false
    const hhbThresholds = weekTask.value.present_item.map(item => Number(item.num))
    const ccbThresholds = weekCcbTask.value.present_item.map(item => Number(item.num))
    const hasReceivedCount = hasReceived.value.length
    let res = 0
    for (let i = hasReceivedCount; i < hhbThresholds.length; i++) {
      if (weekCcbScore.value >= ccbThresholds[i] && weekHhbScore.value >= hhbThresholds[i]) {
        res++
      } else {
        break
      }
    }
    return res ? `一键领取(${res})` : ""
  })

  // 距离下一周礼包需要条件
  const weekLeftScore = computed(() => {
    if (!weekProcess.value || !weekCcbTask.value || isCompleted.value || !computeWeeklyProcess.value.postPresentItem?.length) return null
    const preLength = computeWeeklyProcess.value.prePresentItem.length
    const activatedLength = computeWeeklyProcess.value.activePresentItem.length
    const nextTotalScore = Number(weekCcbTask.value.present_item[preLength + activatedLength].num)
    const nextPayScore = Number(computeWeeklyProcess.value.postPresentItem[0].num)
    return {
      leftTotalScore: nextTotalScore > weekCcbScore.value ? (nextTotalScore - weekCcbScore.value) / 100 : 0,
      leftPayScore: nextPayScore > weekHhbScore.value ? (nextPayScore - weekHhbScore.value) / 100 : 0
    }
  })

  // 距离下一月礼包需要条件（暂时是最高门槛礼包）
  const monthLeftScore = computed(() => {
    if (!monthProcess.value || !monthCcbTask.value || hasMonthlyReceived.value.length) return null
    const presentLength = monthCcbTask.value.present_item.length
    const nextTotalScore = Number(monthCcbTask.value.present_item[presentLength - 1].num)
    const nextPayScore = Number(monthTask.value?.present_item[presentLength - 1].num)

    const curTotalScore = ccbScore.value.monthCcbScore * 100
    const curPayScore = Number(monthProcess.value.total_score)

    return {
      leftTotalScore: nextTotalScore > curTotalScore ? (nextTotalScore - curTotalScore) / 100 : 0,
      leftPayScore: nextPayScore > curPayScore ? (nextPayScore - curPayScore) / 100 : 0
    }
  })

  // 所有周礼包已解锁
  const allUnlocked = computed(() => {
    if (isCompleted.value) return true
    const lastGift =
      computeWeeklyProcess.value.postPresentItem?.length &&
      computeWeeklyProcess.value.postPresentItem[computeWeeklyProcess.value.postPresentItem.length - 1]

    return lastGift && Number(weekProcess.value?.total_score) >= Number(lastGift?.num)
  })

  // 第n周
  const weekCount = computed(() => {
    if (!weekTask.value?.valid_time) return 0
    const validTime = new Date(weekTask.value.valid_time.replace(/-/g, "/"))
    if (!isValid(validTime)) return 0
    const beginTime = new Date(taskConfig.value.textBeginTime)
    const result = formatDistanceStrict(beginTime, validTime, {
      unit: "day",
      roundingMethod: "ceil"
    }).replace(/\bday(s*)/, "")
    return ~~(Number(result) / 7)
  })

  // 周活动时间
  const timeLimitedText = computed(() => {
    if (!weekTask.value?.valid_time) return ""
    const validTime = new Date(weekTask.value.valid_time.replace(/-/g, "/"))
    if (!isValid(validTime)) return ""
    const beginTime = format(subSeconds(validTime, weekTask.value.circle_length_seconds), "M月d号")
    const endTime = format(subSeconds(validTime, 1), "M月d号")
    return `${beginTime}-${endTime}`
  })

  // 本周活动是最后一周或没有月任务
  const isLastWeek = computed(() => !monthTask.value || weekTask.value?.valid_time === monthTask.value?.valid_time)

  // 距下周活动时间还剩几天
  const leftTimeLimitedText = computed(() => {
    if (!weekTask.value?.valid_time) return ""
    const validTime = new Date(weekTask.value.valid_time.replace(/-/g, "/"))
    if (!isValid(validTime)) return ""
    const curTime = new Date()
    const result = Number(
      formatDistanceStrict(curTime, validTime, {
        unit: "day",
        roundingMethod: "ceil"
      }).replace(/\bday(s?)/, "")
    )
    return `周礼包已全部解锁！${result}天后将开启下一周任务`
  })

  // 月活动时间
  const monthlyTimeLimitedText = computed(() => {
    if (!monthTask.value?.valid_time) return ""
    const validTime = new Date(monthTask.value.valid_time.replace(/-/g, "/"))
    if (!isValid(validTime)) return ""

    const beginTime = format(subSeconds(validTime, monthTask.value.circle_length_seconds), "M月d号")
    const endTime = format(subSeconds(validTime, 1), "M月d号")
    return `${beginTime}-${endTime}`
  })

  // 倒计时
  const countDownText = computed(() => {
    if (!monthTask.value && !weekTask.value) return "-"
    const curTime = new Date()
    const validTime = new Date(
      monthTask.value ? monthTask.value.valid_time.replace(/-/g, "/") : weekTask.value!.valid_time.replace(/-/g, "/")
    )
    const result = formatDistanceStrict(curTime, validTime, {
      unit: "day",
      roundingMethod: "ceil"
    }).replace(/\bday(s?)/, "")
    return result
  })

  // 可领取月礼包
  const monthlyPresentReceivable = computed(() => {
    // 目前月任务只有一个奖励
    if (!monthTask.value || !monthProcess.value || !ccbMonthProcess || hasMonthlyReceived.value.length) return false
    const presentLength = monthTask.value?.present_item.length
    const ccbThreshold = Number(monthCcbTask.value?.present_item[presentLength - 1].num)
    const hhbThreshold = Number(monthTask.value?.present_item[presentLength - 1].num)
    const ccbProcess = Number(ccbMonthProcess.value?.total_score) || 0
    const hhbProcess = Number(monthProcess.value.total_score) || 0
    return ccbProcess >= ccbThreshold && hhbProcess >= hhbThreshold
  })

  const zoneText = computed(() => {
    if (!isWeeklyRegistered.value && !isMonthlyRegistered.value) return ""
    if (selectedRole.value && selectedZone.value) return `${selectedZone.value?.zone_name}-${selectedRole.value?.role_name}`
    return "请前往开启任务并选择礼包领取的游戏角色"
  })

  const buttonText = computed(() => {
    if (weekTask.value && !isWeeklyRegistered.value && !requestFailed.value) return "开启任务"
    if (allUnlocked.value) return "查看任务"
    return "前往任务"
  })

  const init = async () => {
    if (!taskConfig.value || totalTaskError.value) return
    isLoading.value = true
    await mutations[MUTATION_TYPES.CLEAR_ZONE]()
    try {
      await Promise.all([handleHhbTaskProcQuery(), handleCcbTaskProcQuery()])
      newbieTask.value = await getNewbieTask()
      if (isShow.value) {
        const cgi_data = taskConfig.value.monthProcess?.cgi_data || taskConfig.value.weekProcess?.cgi_data
        cgi_data && setUserZoneRoleFromCGIData(cgi_data)
        // PersistZoneRole(taskConfig.value)
      }
    } catch (e: any) {
      reportError(e) // 主动上报错误
      requestFailed.value = true
    } finally {
      isLoading.value = false
    }
  }

  // 查询花花币任务档位
  const handleHhbTaskProcQuery = async () => {
    if (!taskConfig.value) return
    const { task_detail } = await taskProcQuery(taskConfig.value)
    const _weekTask = task_detail?.buytask?.task?.rule_item?.find(item => item.task_id === taskConfig.value.taskId)
    const _monthTask =
      monthTaskValid.value && task_detail?.buytask?.task?.rule_item?.find(item => item.task_id === monthConfig.value?.monthTaskId)
    if (!_weekTask && !_monthTask) throw new Error("No task")
    if (_weekTask) {
      weekTask.value = _weekTask
    }
    if (_monthTask && monthTaskValid.value) {
      monthTask.value = _monthTask
    }
  }

  // 查询充充币任务档位
  const handleCcbTaskProcQuery = async () => {
    if (!taskConfig.value) return
    const { task_detail } = await ccbTaskProcQuery(taskConfig.value)
    const _weekCcbTask = task_detail?.buytask?.task?.rule_item?.find(item => item.task_id === giftTaskConfig.value.taskId)
    const _monthCcbTask = task_detail?.buytask?.task?.rule_item?.find(item => item.task_id === giftTaskConfig.value?.monthTaskId)
    if (!_weekCcbTask && !_monthCcbTask) throw new Error("No task")
    if (_weekCcbTask) {
      weekCcbTask.value = _weekCcbTask
      ladderTask.value = await getLadderTask({
        taskId: taskConfig.value.taskId,
        groupId: taskConfig.value.groupId,
        weekCcbTask: weekCcbTask.value,
        offerId: taskConfig.value.offerId
      })
    }
    if (_monthCcbTask && monthTaskValid.value) {
      monthCcbTask.value = _monthCcbTask
    }
  }

  const handleLocationChange = () => {
    const iformat = buttonText.value === "开启任务" ? REPORT.CLICK.START_MISSION_PAGE : REPORT.CLICK.ENTER_MISSION_PAGE
    try {
      reportUserAction({
        iformat,
        resultInfo: {
          actKey: taskConfig.value.groupId
        }
      })
    } catch (e: any) {
      reportError(e) // 主动上报错误
    }
    if (taskConfig.value) {
      if (taskConfig.value) {
        if (buttonText.value === "开启任务") {
          location.href = taskConfig.value.missionPageUrl + "&show_zone=1"
        } else {
          location.href = taskConfig.value.missionPageUrl
        }
      }
    }
  }

  const goMissionPage = () => {
    location.href = taskConfig.value.missionPageUrl
  }

  const handleGetTaskPresent = async (params: {
    offerId: string
    taskId: string
    groupId: string
    presentRank: string
  }): Promise<boolean> => {
    try {
      isLoading.value = true
      await getTaskPresent(params)
      reportUserAction({
        iformat: REPORT.CLICK.RECEIVE_PACKAGE,
        resultInfo: {
          actKey: taskConfig.value.groupId,
          presentRank: params.presentRank,
          taskId: params.taskId
        }
      })
      setToastMsg({ toastMsg: "领取成功！" })
      isLoading.value = false
      return true
    } catch (e: any) {
      if (e === "缺少大区角色信息") {
        setToastMsg({ toastMsg: e })
      } else {
        setToastMsg({ toastMsg: "领取失败" })
        reportError(e) // 主动上报错误
      }
      isLoading.value = false
      return false
    }
  }

  const handleGetWeekTaskPresent = async () => {
    if (!computeWeeklyProcess.value.activePresentItem?.length) return setToastMsg({ toastMsg: "领取失败" })
    const presentRank = computeWeeklyProcess.value.activePresentItem.map(item => item.num).join(",")
    const { offerId, taskId, groupId } = taskConfig.value
    if (await handleGetTaskPresent({ offerId, taskId, groupId, presentRank })) {
      // 领取成功后回显
      weekProcess.value!.present_rank += `${weekProcess.value!.present_rank ? "," : ""}${presentRank}`
      emit("getTaskPresent", { taskId, presentRank })
    }
  }

  const handleGetMonthTaskPresent = async () => {
    if (!monthTask.value || !monthConfig.value?.monthTaskId) return setToastMsg({ toastMsg: "领取失败" })
    const presentLength = monthTask.value?.present_item.length
    const presentRank = monthTask.value?.present_item[presentLength - 1].num // 产品说后期可能会有月任务多个奖励的情况，这里可以参照上面周任务的思路，目前取最后一个月任务奖励
    const { offerId, groupId } = taskConfig.value
    if (await handleGetTaskPresent({ offerId, taskId: monthConfig.value?.monthTaskId, groupId, presentRank })) {
      // 领取成功后回显
      monthProcess.value!.present_rank += `${monthProcess.value!.present_rank ? "," : ""}${presentRank}`
      emit("getTaskPresent", { monthTaskId: monthConfig.value?.monthTaskId, presentRank })
    }
  }

  watch(
    computed(() => isLoggedIn.value && taskConfig.value && isShow.value),
    async () => {
      if (isLoggedIn.value && isShow.value) {
        init()
        monthProcess.value = taskConfig.value.monthProcess || null
        if (!taskConfig.value.weekProcess && monthProcess.value) {
          // 没有周任务但是有月任务的情况下，默认为0进度
          weekProcess.value = {
            task_id: taskConfig.value.taskId,
            origin: "",
            origin_type: "",
            nick_img: "",
            cgi_data: "",
            present_rank: "",
            task_status: "",
            task_token: "",
            total_score: "0"
          }
        } else {
          weekProcess.value = taskConfig.value.weekProcess || null
        }
      }
    },
    {
      immediate: true
    }
  )

  return {
    monthConfig,
    showTips,
    showItemTips,
    weekProcess,
    monthProcess,
    weekTask,
    weekCcbTask,
    monthTask,
    requestFailed,
    computeWeeklyProcess,
    monthTaskValid,
    isWeeklyRegistered,
    isMonthlyRegistered,
    isCompleted,
    allUnlocked,
    receivableText,
    weekLeftScore,
    monthLeftScore,
    weekCount,
    timeLimitedText,
    zoneText,
    buttonText,
    isLastWeek,
    leftTimeLimitedText,
    monthlyTimeLimitedText,
    countDownText,
    monthlyPresentReceivable,
    hasMonthlyReceived,
    isLoading,
    handleShowTipsBox,
    init,
    handleLocationChange,
    handleGetWeekTaskPresent,
    handleGetMonthTaskPresent,
    newbieTask,
    newbieTaskPresentStatus,
    ladderTask,
    goMissionPage
  }
}
